import type { ProcessMapGraph } from "./domain.js";

/**
 * Swimlane process map as a standalone SVG string. Used by the PDF reports and
 * the dashboard so the map looks identical everywhere. Lanes are roles; steps
 * flow left to right in order; markings carry a label as well as a colour so
 * the map reads the same in print and for colour-blind readers.
 */

export type Marking = ProcessMapGraph["steps"][number]["markings"][number];

export const MARKING_STYLE: Record<Marking, { label: string; stroke: string; fill: string; dash?: string }> = {
  match: { label: "Match", stroke: "#8A8F98", fill: "#FFFFFF" },
  overlap: { label: "Overlap", stroke: "#2F6FDE", fill: "#EAF1FD" },
  gap: { label: "Gap", stroke: "#C2382F", fill: "#FBECEB", dash: "5 4" },
  extra: { label: "Extra step", stroke: "#D9682B", fill: "#FDF0E8" },
  conflict: { label: "Conflict", stroke: "#8E44AD", fill: "#F4ECF8" },
  bottleneck: { label: "Bottleneck", stroke: "#C99A06", fill: "#FDF7E3" },
};

const PRIORITY: Marking[] = ["gap", "conflict", "bottleneck", "overlap", "extra", "match"];

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** Break a label into lines of at most `max` characters. */
function wrap(text: string, max: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > max && cur) {
      lines.push(cur);
      cur = w;
    } else cur = (cur + " " + w).trim();
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = kept[maxLines - 1]!.replace(/.{0,2}$/, "") + "…";
    return kept;
  }
  return lines;
}

export interface SvgOptions {
  width?: number;
  showLegend?: boolean;
  /** Font stack; PDFs use locally installed Noto fonts. */
  font?: string;
}

export function processMapSvg(graph: ProcessMapGraph, opts: SvgOptions = {}): string {
  const font = opts.font ?? "Inter, 'Noto Sans', 'Noto Sans Devanagari', Arial, sans-serif";
  const laneLabelW = 150;
  const boxW = 150;
  const boxH = 62;
  const gapX = 34;
  const laneH = 96;
  const top = 16;
  const steps = [...graph.steps].sort((a, b) => a.order - b.order);
  const lanes = graph.lanes.filter((l) => steps.some((s) => s.laneId === l.id));
  const laneIndex = new Map(lanes.map((l, i) => [l.id, i]));
  const col = new Map(steps.map((s, i) => [s.id, i]));
  const width = Math.max(opts.width ?? 0, laneLabelW + 24 + steps.length * (boxW + gapX));
  const legendH = opts.showLegend === false ? 0 : 44;
  const height = top + lanes.length * laneH + legendH + 16;

  const pos = (id: string) => {
    const s = steps.find((x) => x.id === id)!;
    const x = laneLabelW + 16 + col.get(id)! * (boxW + gapX);
    const y = top + (laneIndex.get(s.laneId) ?? 0) * laneH + (laneH - boxH) / 2;
    return { x, y };
  };

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" font-family="${esc(font)}" role="img" aria-label="${esc(graph.title)}">`,
    `<defs><marker id="pm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill="#6B7280"/></marker></defs>`,
  );
  lanes.forEach((l, i) => {
    const y = top + i * laneH;
    parts.push(
      `<rect x="0" y="${y}" width="${width}" height="${laneH}" fill="${i % 2 ? "#F7F8FA" : "#FFFFFF"}" stroke="#E3E6EB"/>`,
      `<rect x="0" y="${y}" width="${laneLabelW}" height="${laneH}" fill="#F0F2F5" stroke="#E3E6EB"/>`,
    );
    wrap(l.name, 18, 3).forEach((line, k, arr) => {
      parts.push(
        `<text x="12" y="${y + laneH / 2 - ((arr.length - 1) * 15) / 2 + k * 15 + 4}" font-size="12.5" font-weight="600" fill="#1F2937">${esc(line)}</text>`,
      );
    });
  });

  for (const e of graph.edges) {
    if (!col.has(e.from) || !col.has(e.to)) continue;
    const a = pos(e.from);
    const b = pos(e.to);
    const x1 = a.x + boxW;
    const y1 = a.y + boxH / 2;
    const x2 = b.x;
    const y2 = b.y + boxH / 2;
    const mid = x1 + (x2 - x1) / 2;
    const d = y1 === y2 ? `M${x1} ${y1}H${x2 - 2}` : `M${x1} ${y1}H${mid}V${y2}H${x2 - 2}`;
    parts.push(`<path d="${d}" fill="none" stroke="#6B7280" stroke-width="1.3" marker-end="url(#pm-arrow)"/>`);
    if (e.label) parts.push(`<text x="${mid + 4}" y="${Math.min(y1, y2) - 6}" font-size="10.5" fill="#6B7280">${esc(e.label)}</text>`);
  }

  for (const s of steps) {
    const { x, y } = pos(s.id);
    const main = PRIORITY.find((m) => s.markings.includes(m)) ?? "match";
    const st = MARKING_STYLE[main];
    parts.push(
      `<g><title>${esc(`${s.name} — ${s.markings.map((m) => MARKING_STYLE[m].label).join(", ")}${s.note ? ` — ${s.note}` : ""}`)}</title>`,
      `<rect x="${x}" y="${y}" width="${boxW}" height="${boxH}" rx="8" fill="${st.fill}" stroke="${st.stroke}" stroke-width="${main === "match" ? 1.2 : 2}"${st.dash ? ` stroke-dasharray="${st.dash}"` : ""}/>`,
    );
    wrap(s.name, 22, 2).forEach((line, k) => {
      parts.push(`<text x="${x + boxW / 2}" y="${y + 21 + k * 15}" text-anchor="middle" font-size="12" font-weight="600" fill="#111827">${esc(line)}</text>`);
    });
    const tags = s.markings.filter((m) => m !== "match").map((m) => MARKING_STYLE[m].label);
    const sub = [tags.join(" · "), s.mentions > 1 ? `${s.mentions} voices` : s.mentions === 1 ? "1 voice" : ""].filter(Boolean).join(" — ");
    if (sub) parts.push(`<text x="${x + boxW / 2}" y="${y + boxH - 9}" text-anchor="middle" font-size="10.5" fill="#4B5563">${esc(sub.length > 30 ? sub.slice(0, 29) + "…" : sub)}</text>`);
    parts.push("</g>");
  }

  if (opts.showLegend !== false) {
    const ly = top + lanes.length * laneH + 22;
    let lx = 0;
    for (const m of ["match", "overlap", "gap", "extra", "conflict", "bottleneck"] as Marking[]) {
      const st = MARKING_STYLE[m];
      parts.push(
        `<rect x="${lx}" y="${ly - 10}" width="18" height="12" rx="3" fill="${st.fill}" stroke="${st.stroke}" stroke-width="1.6"${st.dash ? ` stroke-dasharray="4 3"` : ""}/>`,
        `<text x="${lx + 24}" y="${ly}" font-size="11" fill="#4B5563">${st.label}</text>`,
      );
      lx += 24 + st.label.length * 6.6 + 22;
    }
  }
  parts.push("</svg>");
  return parts.join("");
}

/** Build a single-interview graph from its process steps. */
export function graphFromSteps(
  title: string,
  steps: Array<{ ord: number; lane: string; name: string; issues: string[]; evidence?: Array<{ turnSeq: number; quote: string }> }>,
): ProcessMapGraph {
  const laneId = (n: string) => n.toLowerCase().replace(/[^a-z0-9\p{L}]+/gu, "-") || "lane";
  const lanes = [...new Map(steps.map((s) => [laneId(s.lane), { id: laneId(s.lane), name: s.lane }])).values()];
  const sorted = [...steps].sort((a, b) => a.ord - b.ord);
  const out = sorted.map((s, i) => {
    const markings: Marking[] = [];
    if (s.issues.includes("bottleneck")) markings.push("bottleneck");
    if (s.issues.includes("workaround")) markings.push("extra");
    if (s.issues.includes("duplicate")) markings.push("overlap");
    if (!markings.length) markings.push("match");
    return {
      id: `s${i + 1}`,
      laneId: laneId(s.lane),
      name: s.name,
      order: i + 1,
      mentions: 1,
      confidence: "low" as const,
      markings,
      note: "",
      evidence: [],
    };
  });
  return { title, lanes, steps: out, edges: out.slice(1).map((s, i) => ({ from: out[i]!.id, to: s.id })), variations: [] };
}
